import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Plus, Search, Edit2, ToggleLeft, ToggleRight, MapPin, Image as ImageIcon } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Label } from "shared/ui/label"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  AreaDto,
  AreaUpsertRequest,
  CodeDescrOption,
  PagedResultDto,
  createArea,
  fetchAreaOptions,
  fetchAreaTypes,
  searchAreas,
  setAreaActive,
  updateArea,
} from "modules/admin/api/areaService"

interface TenantOption {
  id: string
  name: string
}

interface AreaMasterProps {
  currentUser?: {
    name?: string
    tenantId?: string | null
    tenants?: Array<{
      tenantId?: string | null
      id?: string | null
      tenantName?: string | null
      name?: string | null
    }>
  }
}

type FormState = {
  tenantId: string
  code: string
  areaType: string
  nameTh: string
  nameEn: string
  parentId: string | null
  descr: string
  plotPlanFile: string
  latitude: string
  longitude: string
  isActive: boolean
}

const buildInitialForm = (tenantId: string): FormState => ({
  tenantId,
  code: "",
  areaType: "",
  nameTh: "",
  nameEn: "",
  parentId: null,
  descr: "",
  plotPlanFile: "",
  latitude: "",
  longitude: "",
  isActive: true,
})

export default function AreaMaster({ currentUser }: AreaMasterProps) {
  const defaultTenantId = DEFAULT_TENANT_ID
  const tenantOptions = useMemo<TenantOption[]>(() => {
    const tenants = currentUser?.tenants
    if (Array.isArray(tenants) && tenants.length > 0) {
      return tenants
        .map((tenant) => ({
          id: tenant.tenantId ?? tenant.id ?? "",
          name: tenant.tenantName ?? tenant.name ?? tenant.tenantId ?? tenant.id ?? "",
        }))
        .filter((tenant) => tenant.id)
    }

    if (currentUser?.tenantId) {
      return [{ id: currentUser.tenantId, name: currentUser.tenantId }]
    }

    return [{ id: defaultTenantId, name: defaultTenantId }]
  }, [currentUser, defaultTenantId])

  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenantOptions[0]?.id ?? defaultTenantId)
  const [items, setItems] = useState<AreaDto[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all")
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editing, setEditing] = useState<AreaDto | null>(null)
  const [form, setForm] = useState<FormState>(buildInitialForm(tenantOptions[0]?.id ?? defaultTenantId))
  const [parentOptions, setParentOptions] = useState<AreaDto[]>([])
  const [areaTypeOptions, setAreaTypeOptions] = useState<CodeDescrOption[]>([])
  const [areaTypeLoading, setAreaTypeLoading] = useState(false)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [pageSize, totalItems])
  const tenantNameLookup = useMemo(
    () => new Map(tenantOptions.map((tenant) => [tenant.id, tenant.name])),
    [tenantOptions],
  )

  useEffect(() => {
    if (!tenantOptions.some((tenant) => tenant.id === selectedTenantId)) {
      setSelectedTenantId(tenantOptions[0]?.id ?? defaultTenantId)
    }
  }, [defaultTenantId, selectedTenantId, tenantOptions])

  const loadData = useCallback(
    async (currentPage = 1) => {
      if (!selectedTenantId) {
        setItems([])
        setTotalItems(0)
        return
      }

      setIsLoading(true)
      try {
        const result: PagedResultDto<AreaDto> = await searchAreas({
          tenantId: selectedTenantId,
          search: searchTerm || undefined,
          status: statusFilter,
          page: currentPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? currentPage)
      } catch (error) {
        console.error(error)
        toast.error("Unable to load areas.")
      } finally {
        setIsLoading(false)
      }
    },
    [pageSize, searchTerm, selectedTenantId, statusFilter],
  )

  const loadParentOptions = useCallback(async (tenantId: string) => {
    if (!tenantId) {
      setParentOptions([])
      return
    }

    try {
      const options = await fetchAreaOptions(tenantId)
      setParentOptions(options)
    } catch (error) {
      console.error("loadParentOptions failed", error)
      setParentOptions([])
    }
  }, [])

  const loadAreaTypes = useCallback(async (tenantId: string) => {
    if (!tenantId) {
      setAreaTypeOptions([])
      return
    }

    try {
      setAreaTypeLoading(true)
      const types = await fetchAreaTypes(tenantId)
      setAreaTypeOptions(types)
    } catch (error) {
      console.error("loadAreaTypes failed", error)
      setAreaTypeOptions([])
    } finally {
      setAreaTypeLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData(1)
  }, [loadData, selectedTenantId])

  useEffect(() => {
    if (!dialogOpen || !form.tenantId) return
    loadParentOptions(form.tenantId)
    loadAreaTypes(form.tenantId)
  }, [dialogOpen, form.tenantId, loadAreaTypes, loadParentOptions])

  const resetForm = useCallback(() => {
    setEditing(null)
    setForm(buildInitialForm(selectedTenantId || defaultTenantId))
  }, [defaultTenantId, selectedTenantId])

  const openCreate = () => {
    resetForm()
    setDialogOpen(true)
  }

  const openEdit = (item: AreaDto) => {
    setEditing(item)
    setForm({
      tenantId: item.tenantId ?? selectedTenantId ?? defaultTenantId,
      code: item.code,
      areaType: item.areaType,
      nameTh: item.nameTh,
      nameEn: item.nameEn ?? "",
      parentId: item.parentId ?? null,
      descr: item.descr ?? "",
      plotPlanFile: item.plotPlanFile ?? "",
      latitude: item.latitude != null ? String(item.latitude) : "",
      longitude: item.longitude != null ? String(item.longitude) : "",
      isActive: item.isActive,
    })
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.tenantId.trim()) {
      toast.error("Please select a tenant.")
      return
    }

    if (!form.code.trim() || !form.areaType.trim() || !form.nameTh.trim() || !form.nameEn.trim()) {
      toast.error("Please fill in code, area type, Thai name, and English name.")
      return
    }

    setIsSubmitting(true)
    try {
      const payload: AreaUpsertRequest = {
        code: form.code.trim(),
        areaType: form.areaType.trim(),
        nameTh: form.nameTh.trim(),
        nameEn: form.nameEn.trim(),
        parentId: form.parentId || null,
        descr: form.descr.trim() || null,
        plotPlanFile: form.plotPlanFile.trim() || null,
        latitude: form.latitude ? Number.parseFloat(form.latitude) : null,
        longitude: form.longitude ? Number.parseFloat(form.longitude) : null,
        isActive: form.isActive,
      }

      if (editing) {
        await updateArea(editing.areaId, payload, form.tenantId)
        toast.success("Area updated successfully.")
      } else {
        await createArea(payload, form.tenantId)
        toast.success("Area created successfully.")
      }

      setDialogOpen(false)
      setSelectedTenantId(form.tenantId)
      resetForm()
      await loadData(page)
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "Unable to save area."
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggle = async (item: AreaDto) => {
    try {
      await setAreaActive(item.areaId, !item.isActive, item.tenantId || selectedTenantId || defaultTenantId)
      toast.success("Area status updated.")
      await loadData(page)
    } catch (error: any) {
      const message = error?.response?.data ?? "Unable to update area status."
      toast.error(message)
    }
  }

  const resolveParentLabel = useCallback(
    (parentId?: string | null) => parentOptions.find((option) => option.areaId === parentId)?.nameTh ?? "-",
    [parentOptions],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Area Master</h1>
          <p className="text-sm text-gray-600">Manage org departments and bind each area to a tenant.</p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Area
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Area List</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="rounded-md border px-3 py-2 text-sm text-gray-700"
              value={selectedTenantId}
              onChange={(event) => setSelectedTenantId(event.target.value)}
            >
              {tenantOptions.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Search by code or area name"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && loadData(1)}
              />
            </div>
            <select
              className="rounded-md border px-3 py-2 text-sm text-gray-700"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as "all" | "active" | "inactive")}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <Button variant="outline" onClick={() => loadData(1)} disabled={isLoading}>
              Search
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setSearchTerm("")
                setStatusFilter("all")
                loadData(1)
              }}
              disabled={isLoading}
            >
              Clear
            </Button>
          </div>

          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name (TH/EN)</TableHead>
                  <TableHead>Area Type</TableHead>
                  <TableHead>Parent Area</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.areaId}>
                    <TableCell className="text-xs text-gray-600">
                      {tenantNameLookup.get(item.tenantId) ?? item.tenantId}
                    </TableCell>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-gray-900">{item.nameTh}</div>
                      <div className="text-xs text-gray-500">{item.nameEn}</div>
                    </TableCell>
                    <TableCell>{item.areaType}</TableCell>
                    <TableCell className="text-sm text-gray-700">{resolveParentLabel(item.parentId)}</TableCell>
                    <TableCell>
                      <span
                        className={`rounded px-2 py-1 text-xs ${
                          item.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="gap-1">
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggle(item)} className="gap-1">
                        {item.isActive ? (
                          <ToggleRight className="h-4 w-4 text-green-600" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-gray-500" />
                        )}
                        {item.isActive ? "Disable" : "Enable"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                      {isLoading ? "Loading..." : "No areas found."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {items.length} of {totalItems} items
            </span>
            <div className="space-x-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => loadData(page - 1)}>
                Previous
              </Button>
              <span>
                Page {page}/{totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => loadData(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Area" : "Add New Area"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Tenant *</Label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={form.tenantId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    tenantId: event.target.value,
                    parentId: null,
                  }))
                }
                disabled={Boolean(editing)}
              >
                {tenantOptions.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Area Code *</Label>
              <Input value={form.code} onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))} />
            </div>
            <div>
              <Label>Area Type *</Label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={form.areaType}
                onChange={(event) => setForm((current) => ({ ...current, areaType: event.target.value }))}
                disabled={areaTypeLoading}
              >
                <option value="">{areaTypeLoading ? "Loading..." : "- Select area type -"}</option>
                {areaTypeOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.descr ?? option.code}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Area Name (TH) *</Label>
              <Input
                value={form.nameTh}
                onChange={(event) => setForm((current) => ({ ...current, nameTh: event.target.value }))}
              />
            </div>
            <div>
              <Label>Area Name (EN) *</Label>
              <Input
                value={form.nameEn}
                onChange={(event) => setForm((current) => ({ ...current, nameEn: event.target.value }))}
              />
            </div>
            <div>
              <Label>Parent Area</Label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={form.parentId ?? ""}
                onChange={(event) => setForm((current) => ({ ...current, parentId: event.target.value || null }))}
              >
                <option value="">- None -</option>
                {parentOptions
                  .filter((option) => option.areaId !== editing?.areaId)
                  .map((option) => (
                    <option key={option.areaId} value={option.areaId}>
                      {option.nameTh} ({option.code})
                    </option>
                  ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Area Description</Label>
              <Input
                value={form.descr}
                onChange={(event) => setForm((current) => ({ ...current, descr: event.target.value }))}
                placeholder="Describe this area"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-gray-500" />
                Plot Plan File
              </Label>
              <Input
                value={form.plotPlanFile}
                onChange={(event) => setForm((current) => ({ ...current, plotPlanFile: event.target.value }))}
                placeholder="Example: plotplan_zone_a.png"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  Latitude
                </Label>
                <Input
                  value={form.latitude}
                  onChange={(event) => setForm((current) => ({ ...current, latitude: event.target.value }))}
                  placeholder="Example: 13.7563"
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input
                  value={form.longitude}
                  onChange={(event) => setForm((current) => ({ ...current, longitude: event.target.value }))}
                  placeholder="Example: 100.5018"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
